// Shared discriminator: "is this image URL ALREADY a resolved MachineID endpoint,
// or is it a bot-gateway image path that must be re-proxied?"
//
// Two shapes reach the image fetchers, and they must be handled differently:
//
//   Shape 1 — bot-gateway image path carrying the `/api/v1/image/` marker
//             (e.g. `/api/v1/image/SBL500_manual_sk/..._page_047.jpg`, or a full
//             `https://machineid.chat/api/v1/image/...` URL). Bot-internal: it MUST be
//             re-proxied through `{apiBaseUrl}/v1/bot/image/{stripped}` so our server
//             attaches auth.
//   Shape 2 — a URL the CALLER already built against our own API, chiefly the diagram
//             page-image endpoint `{apiBaseUrl}/v1/bot/diagram/{page}/image`
//             (MidasArtifactThumbnail, ChatBotMessageV3, resolveKioskArtifactImageUrl).
//             It MUST be fetched as-is.
//
// Why this file exists (regression fixed 2026-08-19): the discriminator used to be
// `startsWith("http")`, which only worked because `apiBaseUrl` was absolute. When
// config.prod.js went root-relative (`/api/account`, TAJMAC-PILOT-CFG) a shape-2 URL
// became `/api/account/v1/bot/diagram/3/image` — no longer http-prefixed — so it was
// misclassified as shape 1 and rewritten to
// `/api/account/v1/bot/image/api/account/v1/bot/diagram/3/image` → 404. That broke
// diagram thumbnails, V3 response source cards and kiosk artifact images in prod.
//
// NOTE the leading slash alone is NOT a valid discriminator: a shape-1 path such as
// `/api/v1/image/foo.jpg` also starts with `/` and still needs the proxy. Nor is
// "absence of the marker" sufficient on its own for root-relative input — a machine_card
// image path like `/v1/customer/machines/{id}/image` carries no marker either, yet has
// its own resolution branch. So for root-relative input we require a POSITIVE match on
// our bot-proxy segment. Behaviour for absolute URLs is deliberately left exactly as it
// was, so this change can only affect the root-relative case it exists to fix.

/** Marker identifying a bot-gateway image path (shape 1 — always needs the proxy). */
export const BOT_IMAGE_MARKER = "/api/v1/image/";

/** Path segment identifying one of OUR bot-proxy endpoints (shape 2 — fetch directly). */
export const BOT_PROXY_SEGMENT = "/v1/bot/";

/**
 * True when `url` is already a fetchable MachineID endpoint and must NOT be rewritten
 * through the `/v1/bot/image/` proxy.
 *
 * - Carries the bot-gateway image marker  → false (proxy it), regardless of origin form.
 * - Absolute `http(s)://…`                → true (unchanged pre-2026-08-19 behaviour).
 * - Root-relative containing `/v1/bot/`   → true (the case relative config broke).
 * - Anything else                         → false (fall through to the caller's own
 *                                           resolution branch / the proxy, as before).
 */
export const isResolvedApiImageUrl = (url: string): boolean => {
  if (!url) return false;
  if (url.includes(BOT_IMAGE_MARKER)) return false;
  if (/^https?:\/\//i.test(url)) return true;
  return url.startsWith("/") && url.includes(BOT_PROXY_SEGMENT);
};
